// 190 §C — ONE HOOK FOR THE THREE BENTOS: the templates a surface may offer,
// the one chosen (remembered per surface in this browser — a UI preference,
// nothing the firm's data depends on), its columns, the placements the
// server holds for it, and the one write a move makes.
//
// 201 §B.3 [RULED — Laurent, 2026-09-21] — THE LADDER OF THE SHOWN TEMPLATE:
// the reader's own pick (`lx:bento:<surface>`, this browser) → the FIRM'S
// DEFAULT for this surface (the model whose meta names the place, set in
// Taxonomies, one per place) → `templates[0]`, the taxonomy's order. And while
// the firm default's « Impose everywhere » is on, THE DEFAULT WINS OVER THE
// PICK AND THE PICK IS NOT ERASED: `setTemplate` writes nothing while imposed
// (the picker refuses with the toggle's own sentence), the stored pick stays
// where it was, and releasing the flag gives everyone their own back. 201 §C:
// the pick stays per browser, per surface — nothing server-side, no per-place
// memory beyond this key.
//
// THE LADDER (ruling 190 (5), C.2 — Eisenhower's own): on the USER surface a
// card sits in MY placement, else the FIRM's, else the first column — so a task
// placed in the case's Bento shows there in mine until I move it myself. On
// the two FIRM-WIDE surfaces (a case's Bento, the list Bento) only the firm's
// placement is read: a firm-wide board is the same board for every colleague,
// and a personal row must not make one viewer's copy differ. The hook hands
// the host the two maps and the columns; the host applies the pure ladder
// (crmDefinitions.mjs bentoColumnOf) per card.
//
// LIVE: a firm-wide move publishes `config` domain `bento` (routes/
// bentoPlacements.js); the App bridges it to `lx:sse`, and every mounted board
// over that template and kind refetches. A personal move is one screen's.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch } from "react";
import { useTaxonomy } from "./useTaxonomy";
import { getBentoPlacements, putBentoPlacement } from "../api";
import type { BentoItemKind, BentoPlacements } from "../api";
import { bentoFirmDefaultFor, bentoTemplatesFor, normalizeBentoTemplateMeta } from "../lib/crmDefinitions.mjs";
import type { BentoColumnDef, BentoMove } from "../components/BentoBoard";

/* global window, localStorage, CustomEvent */

export type BentoSurface = "user" | "case" | "list";

export interface BentoTemplateRow {
  code: string;
  label?: string | null;
  labelEn?: string | null;
  labelFr?: string | null;
  labelNl?: string | null;
  archived?: boolean | null;
  meta?: unknown;
}

const EMPTY: Readonly<Record<string, string>> = Object.freeze({});
const STORE_KEY = (surface: BentoSurface) => `lx:bento:${surface}`;
function readChoice(surface: BentoSurface): string | null {
  try { return localStorage.getItem(STORE_KEY(surface)); } catch { return null; }
}
function writeChoice(surface: BentoSurface, code: string): void {
  try { localStorage.setItem(STORE_KEY(surface), code); } catch { /* a browser with storage disabled keeps the session's choice only */ }
}

export interface BentoState {
  templates: readonly BentoTemplateRow[];
  templateCode: string | null;
  setTemplate: Dispatch<string>;
  columns: readonly BentoColumnDef[];
  /** 190 glance round 2 [C] — the list card's fields, the template's own choice (column keys). */
  fields: readonly string[];
  /** 190 round 6 [S] — the template shows cases AND collective cases merged on either list. */
  allMatters: boolean;
  /** itemId → column, the viewer's own rows — empty on a firm-wide surface. */
  mine: Readonly<Record<string, string>>;
  /** itemId → column, the firm's rows. */
  firm: Readonly<Record<string, string>>;
  /** itemId → column for every row the ladder consults (the "placed" marks). */
  placed: Readonly<Record<string, string>>;
  /** One human move: the placement row, and nothing else. */
  move: Dispatch<BentoMove>;
  loaded: boolean;
  /** 201 §B.3 — the firm's default for this surface is imposed: `templateCode` is its code whatever the pick, and `setTemplate` writes nothing. */
  imposed: boolean;
}

export function useBento(surface: BentoSurface, itemKind: BentoItemKind, open: boolean): BentoState {
  const { entries } = useTaxonomy("bento_template") as { entries: unknown };
  const templates = useMemo(
    () => bentoTemplatesFor(Array.isArray(entries) ? (entries as BentoTemplateRow[]) : [], surface) as BentoTemplateRow[],
    [entries, surface],
  );
  const [chosen, setChosen] = useState<string | null>(() => readChoice(surface));
  const firstCode: string | null = templates.length ? templates[0].code : null;
  // 201 §B.3 — the firm's default for this surface (null when the firm set
  // none) and whether it is imposed; both read off the same rows as `templates`.
  const firmDefault = useMemo(
    () => bentoFirmDefaultFor(Array.isArray(entries) ? (entries as BentoTemplateRow[]) : [], surface) as BentoTemplateRow | null,
    [entries, surface],
  );
  const firmCode: string | null = firmDefault ? firmDefault.code : null;
  const imposed: boolean = firmDefault !== null && normalizeBentoTemplateMeta(firmDefault.meta).impose;
  const chosenCode: string | null = chosen !== null && templates.some((tp) => tp.code === chosen) ? chosen : null;
  const templateCode: string | null = imposed ? firmCode : chosenCode ?? firmCode ?? firstCode;
  const template = templates.find((tp) => tp.code === templateCode);
  const columns = useMemo<readonly BentoColumnDef[]>(() => (template ? normalizeBentoTemplateMeta(template.meta).columns : []), [template]);
  const fields = useMemo<readonly string[]>(() => (template ? normalizeBentoTemplateMeta(template.meta).fields : []), [template]);
  const allMatters = useMemo<boolean>(() => (template ? normalizeBentoTemplateMeta(template.meta).allMatters : false), [template]);
  const [placements, setPlacements] = useState<BentoPlacements | null>(null);
  const refetch = useCallback(() => {
    if (!open || templateCode === null) return;
    getBentoPlacements(templateCode, itemKind).then((p) => { setPlacements(p); }).catch(() => { /* the board keeps what it has; the next open refetches */ });
  }, [open, templateCode, itemKind]);
  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ type?: string; domain?: string; templateCode?: string; itemKind?: BentoItemKind }>).detail;
      if (templateCode !== null && d.type === "config" && d.domain === "bento" && d.templateCode === templateCode && d.itemKind === itemKind) refetch();
    };
    window.addEventListener("lx:sse", h);
    return () => { window.removeEventListener("lx:sse", h); };
  }, [refetch, templateCode, itemKind]);
  const current = useMemo<BentoPlacements | null>(
    () => (placements !== null && placements.templateCode === templateCode && placements.itemKind === itemKind ? placements : null),
    [placements, templateCode, itemKind],
  );
  const mine = useMemo<Readonly<Record<string, string>>>(() => (surface === "user" && current ? current.mine : EMPTY), [surface, current]);
  const firm = useMemo<Readonly<Record<string, string>>>(() => (current ? current.firm : EMPTY), [current]);
  const placed = useMemo<Readonly<Record<string, string>>>(() => (surface === "user" ? { ...firm, ...mine } : firm), [surface, firm, mine]);
  const move = useCallback<Dispatch<BentoMove>>(({ itemId: rawId, column }) => {
    if (templateCode === null) return;
    // 190 round 6 [S] — a board that merges two kinds hands its host's
    // composite id (`ca:12`) to the HOST's dispatcher, never here: this hook
    // is one kind's, and its placement row names a numeric item id.
    const itemId = typeof rawId === "number" ? rawId : Number.parseInt(rawId.replace(/^[a-z]+:/, ""), 10);
    if (!Number.isFinite(itemId)) return;
    const code = templateCode;
    const scope = surface === "user" ? "mine" : "firm";
    // Optimistic: the card lands where it was dropped; the server's answer (or
    // the next refetch) is the truth.
    setPlacements((prev) => {
      const base: BentoPlacements = prev !== null && prev.templateCode === code && prev.itemKind === itemKind ? prev : { templateCode: code, itemKind, firm: {}, mine: {} };
      return scope === "mine"
        ? { ...base, mine: { ...base.mine, [String(itemId)]: column } }
        : { ...base, firm: { ...base.firm, [String(itemId)]: column } };
    });
    putBentoPlacement({ templateCode: code, itemKind, itemId, column, scope }).then(refetch, refetch);
  }, [templateCode, itemKind, surface, refetch]);
  // 201 §B.3 — while imposed, a pick is REFUSED here too (the picker says why):
  // the stored choice is neither replaced nor erased, so releasing the flag
  // returns exactly what the reader had.
  const setTemplate = useCallback<Dispatch<string>>((code) => { if (!imposed) { setChosen(code); writeChoice(surface, code); } }, [surface, imposed]);
  return { templates, templateCode, setTemplate, columns, fields, allMatters, mine, firm, placed, move, loaded: current !== null, imposed };
}
